import { create } from 'zustand';

export type ChatDockTab = 'global' | 'clan' | 'dms';

interface ChatDockState {
  open: boolean;
  tab: ChatDockTab;
  /** Currently-open DM thread (friend's userId). Null = friend list view. */
  dmWith: string | null;
  /** Unread counts, used when the dock is collapsed. */
  unreadGlobal: number;
  unreadClan: number;
  unreadDmByUser: Record<string, number>;
  /** Pending incoming friend requests count — drives the bubble badge so the
   * user notices an invite even with the dock closed. */
  pendingRequests: number;

  setOpen: (v: boolean) => void;
  toggle: () => void;
  setTab: (t: ChatDockTab) => void;
  openDm: (userId: string) => void;
  closeDm: () => void;
  bumpGlobal: () => void;
  bumpClan: () => void;
  bumpDm: (userId: string) => void;
  clearGlobal: () => void;
  clearClan: () => void;
  clearDm: (userId: string) => void;
  setPendingRequests: (n: number) => void;
  bumpPendingRequests: () => void;
  totalUnread: () => number;
}

export const useChatDock = create<ChatDockState>((set, get) => ({
  open: false,
  tab: 'global',
  dmWith: null,
  unreadGlobal: 0,
  unreadClan: 0,
  unreadDmByUser: {},
  pendingRequests: 0,

  setOpen: (v) => set({ open: v }),
  toggle: () => set((s) => ({ open: !s.open })),
  setTab: (t) => set({ tab: t }),
  openDm: (userId) => set({ tab: 'dms', dmWith: userId, open: true }),
  closeDm: () => set({ dmWith: null }),
  bumpGlobal: () => set((s) => ({ unreadGlobal: s.unreadGlobal + 1 })),
  bumpClan: () => set((s) => ({ unreadClan: s.unreadClan + 1 })),
  bumpDm: (userId) =>
    set((s) => ({
      unreadDmByUser: {
        ...s.unreadDmByUser,
        [userId]: (s.unreadDmByUser[userId] ?? 0) + 1,
      },
    })),
  clearGlobal: () => set({ unreadGlobal: 0 }),
  clearClan: () => set({ unreadClan: 0 }),
  clearDm: (userId) =>
    set((s) => {
      const next = { ...s.unreadDmByUser };
      delete next[userId];
      return { unreadDmByUser: next };
    }),
  setPendingRequests: (n) => set({ pendingRequests: Math.max(0, n) }),
  bumpPendingRequests: () => set((s) => ({ pendingRequests: s.pendingRequests + 1 })),
  totalUnread: () => {
    const s = get();
    const dm = Object.values(s.unreadDmByUser).reduce((a, b) => a + b, 0);
    return s.unreadGlobal + s.unreadClan + dm + s.pendingRequests;
  },
}));
